// src/modules/time-tracking/timeTrackingService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL + '/time-tracking';
const SETTINGS_URL = process.env.REACT_APP_API_URL + '/settings';
const getAuthToken = () => localStorage.getItem('token');
const getHeaders = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

// ---- Admin: reports ----
// Returns { shifts, total, limit, offset }. Supports filtering by employee,
// date range, status, "pending review" (reviewOnly), plus offset pagination.
export const getReportShifts = async ({ userId, from, to, reviewOnly, status, limit, offset } = {}) => {
  try {
    const q = new URLSearchParams();
    if (userId) q.append('userId', userId);
    if (from) q.append('from', from);
    if (to) q.append('to', to);
    if (reviewOnly) q.append('reviewOnly', 'true');
    if (status) q.append('status', status);
    if (limit != null) q.append('limit', limit);
    if (offset != null) q.append('offset', offset);
    const response = await axios.get(`${API_URL}/reports/shifts?${q.toString()}`, getHeaders());
    return response.data;
  } catch (error) { console.error('Error fetching shift report:', error); throw error; }
};

export const getReportShift = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/reports/shifts/${id}`, getHeaders());
    return response.data;
  } catch (error) { console.error(`Error fetching shift ${id}:`, error); throw error; }
};

// Admin payroll: { employees: [{ user, weeks, totals }] } grouped Mon–Sun.
export const getPayrollReport = async ({ userId, from, to } = {}) => {
  try {
    const q = new URLSearchParams();
    if (userId) q.append('userId', userId);
    if (from) q.append('from', from);
    if (to) q.append('to', to);
    const response = await axios.get(`${API_URL}/reports/payroll?${q.toString()}`, getHeaders());
    return response.data;
  } catch (error) { console.error('Error fetching payroll report:', error); throw error; }
};

// ---- Admin: register (shift) CRUD ----
export const createShift = async (data) => {
  try {
    const response = await axios.post(`${API_URL}/reports/shifts`, data, getHeaders());
    return response.data;
  } catch (error) { console.error('Error creating shift:', error); throw error; }
};

export const updateShift = async (id, data) => {
  try {
    const response = await axios.put(`${API_URL}/reports/shifts/${id}`, data, getHeaders());
    return response.data;
  } catch (error) { console.error(`Error updating shift ${id}:`, error); throw error; }
};

export const deleteShift = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/reports/shifts/${id}`, getHeaders());
    return response.data;
  } catch (error) { console.error(`Error deleting shift ${id}:`, error); throw error; }
};

export const getLive = async () => {
  try {
    const response = await axios.get(`${API_URL}/reports/live`, getHeaders());
    return response.data;
  } catch (error) { console.error('Error fetching live view:', error); throw error; }
};

// ---- Admin: allowed sites (geofences) ----
export const listSites = async () => {
  try {
    const response = await axios.get(`${API_URL}/sites`, getHeaders());
    return response.data;
  } catch (error) { console.error('Error listing sites:', error); throw error; }
};

export const createSite = async (data) => {
  try {
    const response = await axios.post(`${API_URL}/sites`, data, getHeaders());
    return response.data;
  } catch (error) { console.error('Error creating site:', error); throw error; }
};

export const updateSite = async (id, data) => {
  try {
    const response = await axios.put(`${API_URL}/sites/${id}`, data, getHeaders());
    return response.data;
  } catch (error) { console.error(`Error updating site ${id}:`, error); throw error; }
};

export const deleteSite = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/sites/${id}`, getHeaders());
    return response.data;
  } catch (error) { console.error(`Error deleting site ${id}:`, error); throw error; }
};

// ---- Admin: standing jobs (permanent internal clock-in targets) ----
// A standing job always appears in the crew's clock-in job list, every day, so
// they can clock in when nothing is scheduled. The geofence still applies.
export const listStandingJobs = async () => {
  try {
    const response = await axios.get(`${API_URL}/standing-jobs`, getHeaders());
    return response.data;
  } catch (error) { console.error('Error listing standing jobs:', error); throw error; }
};

export const createStandingJob = async (data) => {
  try {
    const response = await axios.post(`${API_URL}/standing-jobs`, data, getHeaders());
    return response.data;
  } catch (error) { console.error('Error creating standing job:', error); throw error; }
};

export const updateStandingJob = async (id, data) => {
  try {
    const response = await axios.put(`${API_URL}/standing-jobs/${id}`, data, getHeaders());
    return response.data;
  } catch (error) { console.error(`Error updating standing job ${id}:`, error); throw error; }
};

export const deleteStandingJob = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/standing-jobs/${id}`, getHeaders());
    return response.data;
  } catch (error) { console.error(`Error deleting standing job ${id}:`, error); throw error; }
};

// ---- Employee: self ----
export const getMyCurrent = async () => {
  try {
    const response = await axios.get(`${API_URL}/me/current`, getHeaders());
    return response.data;
  } catch (error) { console.error('Error fetching current shift:', error); throw error; }
};

export const getMyShifts = async ({ from, to } = {}) => {
  try {
    const q = new URLSearchParams();
    if (from) q.append('from', from);
    if (to) q.append('to', to);
    const response = await axios.get(`${API_URL}/me/shifts?${q.toString()}`, getHeaders());
    return response.data;
  } catch (error) { console.error('Error fetching my shifts:', error); throw error; }
};

export const getMyShift = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/me/shifts/${id}`, getHeaders());
    return response.data;
  } catch (error) { console.error(`Error fetching my shift ${id}:`, error); throw error; }
};

// Employee's own payroll: a single { user, weeks, totals } object.
export const getMyPayroll = async ({ from, to } = {}) => {
  try {
    const q = new URLSearchParams();
    if (from) q.append('from', from);
    if (to) q.append('to', to);
    const response = await axios.get(`${API_URL}/me/payroll?${q.toString()}`, getHeaders());
    return response.data;
  } catch (error) { console.error('Error fetching my payroll:', error); throw error; }
};

// ---- Admin: time-tracking preferences (stored in the shared Settings k/v store
// under key `time_tracking_prefs`; consumed as form defaults). Backend has no
// dedicated config, so these are admin-set frontend defaults. ----
const PREFS_KEY = 'time_tracking_prefs';
export const DEFAULT_TT_PREFS = { id: null, default_radius_feet: 600, auto_create_site: false };

export const getTimeTrackingPrefs = async () => {
  try {
    const response = await axios.get(SETTINGS_URL, getHeaders());
    const rec = (response.data || []).find((s) => s.key === PREFS_KEY);
    const value = rec?.value || {};
    const radius = parseInt(value.default_radius_feet, 10);
    return {
      id: rec?.id ?? null,
      default_radius_feet: Number.isFinite(radius) && radius > 0 ? radius : 600,
      auto_create_site: value.auto_create_site === true || value.auto_create_site === 'true',
    };
  } catch (error) {
    console.error('Error loading time-tracking prefs:', error);
    return { ...DEFAULT_TT_PREFS };
  }
};

export const saveTimeTrackingPrefs = async ({ id, default_radius_feet, auto_create_site }) => {
  const value = {
    default_radius_feet: parseInt(default_radius_feet, 10) || 600,
    auto_create_site: !!auto_create_site,
  };
  try {
    if (id) {
      const response = await axios.put(`${SETTINGS_URL}/${id}`, { key: PREFS_KEY, value }, getHeaders());
      return response.data;
    }
    const response = await axios.post(SETTINGS_URL, { key: PREFS_KEY, value }, getHeaders());
    return response.data;
  } catch (error) { console.error('Error saving time-tracking prefs:', error); throw error; }
};

// ---- helpers ----
export const formatHours = (seconds) => {
  if (seconds === undefined || seconds === null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

// Duration of a shift in seconds — stored total, or live-computed if still active.
export const shiftSeconds = (shift) => {
  if (!shift) return 0;
  if (shift.total_seconds != null) return shift.total_seconds;
  const start = new Date(shift.clock_in_at).getTime();
  return Math.max(0, Math.round((Date.now() - start) / 1000));
};

export const statusColor = (status) => ({
  active: '#3498db',
  completed: '#84c959',
  auto_closed: '#f39c12',
}[status] || '#7f8c8d');

export const statusLabel = (status) => ({
  active: 'Active',
  completed: 'Completed',
  auto_closed: 'Auto-closed',
}[status] || status);

export const activityColor = (a) => ({
  at_site: '#84c959',
  stationary: '#f39c12',
  moving: '#3498db',
  driving: '#e74c3c',
  unknown: '#7f8c8d',
}[a] || '#7f8c8d');
